import React, { useState } from 'react';
import {
  ArrowLeft,
  TrendingUp,
  Award,
  BookOpen,
  Clock,
  Target,
  Download,
  Upload,
  BarChart3,
  PieChart,
  Calendar,
  Zap,
} from 'lucide-react';
import { useApp } from '../../lib/AppContext';
import { DifficultyLevel, VocabularyWord } from '../../types';
import { WeeklyChart, StreakDisplay } from './ProgressCharts';
import { VocabularyEditor } from './VocabularyEditor';
import { storageService } from '../../services/storageService';

interface AdminDashboardProps {
  onBack: () => void;
}

const LEVEL_INFO = {
  1: { name: 'Beginner', emoji: '🌟', color: 'bg-green-500' },
  2: { name: 'Easy', emoji: '⭐', color: 'bg-blue-500' },
  3: { name: 'Medium', emoji: '🏅', color: 'bg-yellow-500' },
  4: { name: 'Hard', emoji: '🏆', color: 'bg-orange-500' },
  5: { name: 'Expert', emoji: '👑', color: 'bg-red-500' },
};

export function AdminDashboard({ onBack }: AdminDashboardProps) {
  const { userProfile, userProgress } = useApp();
  const [activeTab, setActiveTab] = useState<'overview' | 'progress' | 'analytics' | 'vocabulary'>('overview');
  const [customWords, setCustomWords] = useState(storageService.getCustomVocabulary());

  if (!userProfile || !userProgress) return null;

  const totalWords = userProgress.wordsLearned.length;
  const averageScore = userProgress.gamesPlayed > 0
    ? Math.round(userProgress.totalScore / userProgress.gamesPlayed)
    : 0;

  const unlockedLevels = Object.values(userProgress.levelProgress).filter(p => p.unlocked).length;
  const totalStars = Object.values(userProgress.levelProgress).reduce((sum, p) => sum + p.stars, 0);

  const handleAddWords = (words: VocabularyWord[]) => {
    const updated = storageService.addCustomWords(words);
    setCustomWords(updated);
  };

  const handleDeleteWord = (wordId: string) => {
    const updated = storageService.deleteCustomWord(wordId);
    setCustomWords(updated);
  };

  const exportData = () => {
    const data = {
      profile: userProfile,
      progress: userProgress,
      exportedAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `word-adventure-${userProfile.name}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importData = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const data = JSON.parse(event.target?.result as string);
            if (confirm('This will overwrite your current progress. Continue?')) {
              localStorage.setItem('wa_userProfile', JSON.stringify(data.profile));
              localStorage.setItem('wa_userProgress', JSON.stringify(data.progress));
              window.location.reload();
            }
          } catch (err) {
            alert('Invalid file format');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-3xl shadow-2xl p-8 mb-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-4 transition-colors"
            >
              <ArrowLeft size={20} />
              Back to Home
            </button>
            <h1 className="text-4xl font-bold text-primary">Parent Dashboard</h1>
            <p className="text-gray-600 mt-2">Track progress and manage learning</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={exportData}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors"
            >
              <Download size={20} />
              Export Data
            </button>
            <button
              onClick={importData}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors"
            >
              <Upload size={20} />
              Import Data
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'overview'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('progress')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'progress'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Detailed Progress
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'analytics'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Analytics
          </button>
          <button
            onClick={() => setActiveTab('vocabulary')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'vocabulary'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Vocabulary
          </button>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-3xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="text-5xl">⭐</div>
                <Award size={32} className="opacity-75" />
              </div>
              <div className="text-4xl font-bold mb-1">{userProgress.totalScore}</div>
              <div className="text-yellow-100 text-sm">Total Score</div>
            </div>

            <div className="bg-gradient-to-br from-blue-400 to-blue-500 rounded-3xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="text-5xl">🎮</div>
                <TrendingUp size={32} className="opacity-75" />
              </div>
              <div className="text-4xl font-bold mb-1">{userProgress.gamesPlayed}</div>
              <div className="text-blue-100 text-sm">Games Played</div>
            </div>

            <div className="bg-gradient-to-br from-green-400 to-green-500 rounded-3xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="text-5xl">📚</div>
                <BookOpen size={32} className="opacity-75" />
              </div>
              <div className="text-4xl font-bold mb-1">{totalWords}</div>
              <div className="text-green-100 text-sm">Words Learned</div>
            </div>

            <div className="bg-gradient-to-br from-purple-400 to-purple-500 rounded-3xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="text-5xl">🏆</div>
                <Target size={32} className="opacity-75" />
              </div>
              <div className="text-4xl font-bold mb-1">{totalStars}/15</div>
              <div className="text-purple-100 text-sm">Total Stars</div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-3xl shadow-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <BarChart3 className="text-purple-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Performance</h3>
                  <p className="text-sm text-gray-600">Learning metrics</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Average Score per Game</span>
                  <span className="text-2xl font-bold text-purple-600">{averageScore}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Levels Unlocked</span>
                  <span className="text-2xl font-bold text-blue-600">{unlockedLevels}/5</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Achievements</span>
                  <span className="text-2xl font-bold text-yellow-600">
                    {userProgress.achievements.length}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl shadow-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-green-100 rounded-xl">
                  <Zap className="text-green-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Activity</h3>
                  <p className="text-sm text-gray-600">Learning activity</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Current Language</span>
                  <span className="text-xl font-bold text-gray-800">
                    {userProfile.preferredLanguage.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Member Since</span>
                  <span className="text-xl font-bold text-gray-800">
                    {new Date(userProfile.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Last Played</span>
                  <span className="text-xl font-bold text-gray-800">
                    {new Date(userProgress.lastPlayed).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Level Overview */}
          <div className="bg-white rounded-3xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-orange-100 rounded-xl">
                <PieChart className="text-orange-600" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Level Progress</h3>
                <p className="text-sm text-gray-600">Performance by difficulty</p>
              </div>
            </div>
            <div className="grid grid-cols-5 gap-4">
              {([1, 2, 3, 4, 5] as DifficultyLevel[]).map((level) => {
                const info = LEVEL_INFO[level];
                const progress = userProgress.levelProgress[level];
                return (
                  <div
                    key={level}
                    className={`rounded-2xl p-4 text-center ${
                      progress.unlocked ? 'bg-gradient-to-br ' + info.color : 'bg-gray-200'
                    } text-white`}
                  >
                    <div className="text-4xl mb-2">{info.emoji}</div>
                    <div className="font-bold mb-1">{info.name}</div>
                    {progress.unlocked ? (
                      <>
                        <div className="text-2xl font-bold">{progress.completionPercentage}%</div>
                        <div className="text-sm opacity-90">{'⭐'.repeat(progress.stars)}</div>
                      </>
                    ) : (
                      <div className="text-sm">🔒 Locked</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Detailed Progress Tab */}
      {activeTab === 'progress' && (
        <div className="space-y-6">
          <div className="bg-white rounded-3xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Detailed Level Analysis</h2>
            <div className="space-y-6">
              {([1, 2, 3, 4, 5] as DifficultyLevel[]).map((level) => {
                const info = LEVEL_INFO[level];
                const progress = userProgress.levelProgress[level];
                return (
                  <div key={level} className="border-b border-gray-200 pb-6 last:border-0">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className="text-5xl">{info.emoji}</div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-800">
                            Level {level}: {info.name}
                          </h3>
                          <p className="text-gray-600">
                            {progress.unlocked ? 'Unlocked' : '🔒 Locked'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-4xl font-bold text-gray-800">
                          {progress.completionPercentage}%
                        </div>
                        <div className="text-yellow-500 text-2xl">
                          {'⭐'.repeat(progress.stars)}
                          {'☆'.repeat(3 - progress.stars)}
                        </div>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div
                        className={`h-4 rounded-full ${info.color} transition-all duration-500`}
                        style={{ width: `${progress.completionPercentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Achievements */}
          <div className="bg-white rounded-3xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Achievements</h2>
            {userProgress.achievements.length > 0 ? (
              <div className="grid md:grid-cols-3 gap-4">
                {userProgress.achievements.map((achievement) => (
                  <div
                    key={achievement.id}
                    className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 border-2 border-yellow-300"
                  >
                    <div className="text-4xl mb-2 text-center">{achievement.icon}</div>
                    <h4 className="font-bold text-center text-gray-800">{achievement.name}</h4>
                    <p className="text-sm text-gray-600 text-center mt-1">
                      {achievement.description}
                    </p>
                    {achievement.unlockedAt && (
                      <p className="text-xs text-gray-500 text-center mt-2">
                        {new Date(achievement.unlockedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <div className="text-6xl mb-4">🏆</div>
                <p>No achievements yet. Keep playing to unlock them!</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          {/* Streak and Daily Goal */}
          <StreakDisplay
            currentStreak={userProgress.currentStreak}
            longestStreak={userProgress.longestStreak}
            dailyGoal={userProgress.dailyGoal}
            dailyProgress={userProgress.dailyProgress}
          />

          {/* Weekly Charts */}
          <div className="grid md:grid-cols-2 gap-6">
            <WeeklyChart
              data={userProgress.weeklyGames}
              labels={['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']}
              title="Weekly Games"
              color="bg-gradient-to-t from-blue-400 to-blue-500"
            />
            <WeeklyChart
              data={userProgress.weeklyScores}
              labels={['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']}
              title="Weekly Score"
              color="bg-gradient-to-t from-yellow-400 to-yellow-500"
            />
          </div>

          {/* Insights */}
          <div className="bg-white rounded-3xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Learning Insights</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6">
                <div className="text-4xl mb-3">📅</div>
                <h3 className="font-bold text-gray-800 mb-2">Most Active Day</h3>
                <p className="text-2xl font-bold text-purple-600">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][
                    userProgress.weeklyGames.indexOf(Math.max(...userProgress.weeklyGames))
                  ]}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {Math.max(...userProgress.weeklyGames)} games played
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6">
                <div className="text-4xl mb-3">⚡</div>
                <h3 className="font-bold text-gray-800 mb-2">Weekly Average</h3>
                <p className="text-2xl font-bold text-blue-600">
                  {Math.round(userProgress.weeklyGames.reduce((a, b) => a + b, 0) / 7)} games/day
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {Math.round(userProgress.weeklyScores.reduce((a, b) => a + b, 0) / 7)} pts/day
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6">
                <div className="text-4xl mb-3">🎯</div>
                <h3 className="font-bold text-gray-800 mb-2">Goal Progress</h3>
                <p className="text-2xl font-bold text-green-600">
                  {Math.round((userProgress.dailyProgress / userProgress.dailyGoal) * 100)}%
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  of today's goal ({userProgress.dailyProgress}/{userProgress.dailyGoal} words)
                </p>
              </div>
            </div>

            {/* Motivation Message */}
            <div className="mt-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-4">
                <div className="text-5xl">🌟</div>
                <div>
                  <h3 className="text-xl font-bold mb-1">Keep Up the Great Work!</h3>
                  <p className="text-purple-100">
                    {userProgress.currentStreak > 0
                      ? `You're on a ${userProgress.currentStreak}-day streak! Don't break it!`
                      : "Start playing today to begin a new learning streak!"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vocabulary Tab */}
      {activeTab === 'vocabulary' && (
        <VocabularyEditor
          language={userProfile.preferredLanguage}
          onAddWords={handleAddWords}
          onDeleteWord={handleDeleteWord}
          customWords={customWords}
        />
      )}
    </div>
  );
}
